import React, { useState } from 'react'
import Icon from 'components/Icon'
import classNames from 'classnames'
import { Tooltip, Chip, Button } from '@arnsbogroup/react-voicearchive'
import { FormattedDate, FormattedRelativeTime } from 'react-intl'
import { selectUnit } from '@formatjs/intl-utils'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import { request } from 'hooks/useApi'

export default ({ quote, project, dispatch, onToggleProduct }) => {
  const [loading, setLoading] = useState(false)

  const onClickProduct = (name) => {
    if (!quote?.accepted && onToggleProduct) {
      onToggleProduct(name)
    }
  }

  const downloadQuote = () => {
    setLoading(true)
    request(`/rest/v2/project/${project.id}/quote-document?id=${quote.id}`)
      .then(({ data }) =>{
        setLoading(false)
        if (data.url) {
          window.open(data.url, "_blank")
        }
      })
      .catch((error) => {
        setLoading(false)
        console.log(error)
      })
  }

  const { value, unit } = selectUnit(quote.createdAtTimestamp * 1000)

  return (
    <div className="space-y-2">
      {quote.accepted ? (
        <h1 className="text-gray-400 font-medium text-sm space-x-2">
          <Icon icon={['fas', 'circle-check']} />
          <span>
            <FormattedMessage {...messages.quoteAccepted}/>
          </span>
        </h1>
      ) : (
        <h1 className="text-primary font-medium text-sm space-x-2">
          <Icon icon="circle" />
          <span>
            <FormattedMessage {...messages.quoteNotAccepted}/>
          </span>
        </h1>
      )}

      <div
        className={classNames('rounded space-y-4 z-40 relative sm:flex', {
          'bg-yellow-100 shadow-lg': !quote.accepted,
          'border border-gray-300 bg-gray-100 shadow-lg': quote.accepted,
        })}
      >
        <div
          className={classNames('w-full sm:w-2/3 space-y-2 p-4', {
          })}
        >
          <div className="grid grid-cols-2 gap-2">
            {quote.products
              .filter(({ selected, service }) =>
                quote.accepted ? selected : service || selected
              )
              .map(
                ({
                  id,
                  name,
                  price,
                  currency,
                  optional,
                  selected,
                  service,
                  description,
                  note,
                  total,
                }) => (
                  <div
                    className={classNames('relative rounded p-2 bg-white', {
                      'cursor-pointer hover:bg-gray-50': optional,
                      'cursor-not-allowed': !optional,
                    })}
                    key={`product-${id}`}
                    onClick={() => onClickProduct(name)}
                  >
                    <div className="flex justify-between items-center">
                      <div className="text-sm">
                        <div className="font-medium flex space-x-2">
                          <span>{name}</span>

                          { !!description?.length &&
                            <Tooltip tooltip={description} className="max-w-sm z-50">
                              <Icon
                                title={description}
                                icon={['fas', 'info-circle']}
                                className="text-primary hover:text-primary-lighter"
                              />
                            </Tooltip>
                          }
                        </div>
                        <div className="space-x-2">
                          <span className="font-bold text-primary">{price || total} {currency}</span>
                          { note &&
                            <span className="text-grayscale-lighter">{ note }</span>
                          }
                        </div>
                      </div>
                      <Icon
                        icon={selected ? ['far', 'circle-check'] : 'circle'}
                        className="text-primary hover:text-primary-darker mx-2 text-lg"
                      />
                    </div>

                    {optional && service && !selected && (
                      <Chip className="absolute top-0 right-0 -mr-4 -mt-2 z-40">
                        <FormattedMessage {...messages.Recommended}/>
                      </Chip>
                    )}
                  </div>
                )
              )}
          </div>
        </div>
        <div className="w-full sm:w-1/3 flex flex-col">
          <div className="space-y-2 flex-grow px-4">
            <h1 className="text-sm flex justify-between">
              <div className="font-bold">
                {quote.title}
                {quote?.accepted && (
                  <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform bg-positive rounded-full shadow">
                    Accepted
                  </span>
                )}
              </div>
              <div className='flex gap-2'>
                { new Date(quote.createdAtTimestamp * 1000).getFullYear() !== new Date().getFullYear() ? (
                  <FormattedDate value={new Date(quote.createdAtTimestamp * 1000)} />
                ) : (
                  <FormattedRelativeTime value={value} unit={unit} />
                ) }
                <Button
                  className="px-2"
                  variant="inline"
                  state="tertiary"
                  onClick={downloadQuote}
                  icon={['fa', 'download']}
                  iconPosition="right"
                  disabled={loading}
                >
                  {!loading ? (
                    <FormattedMessage {...messages.downloadQuote}/>
                  ) : (
                    <>
                      <FormattedMessage {...messages.loading}/>
                    </>
                  )}
                </Button>
              </div>
            </h1>
            { quote.body &&
              <div
                className="text-sm space-y-2 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-4 [&_ol]:pl-4 [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: quote.body.trim() }}
              />
            }
            { quote.deliveryTimeEstimate !== '' &&
              <div>
                  <div className='flex justify-end gap-2'>
                    <div className="font-medium text-xs text-gray-600">Delivery time estimate: </div>
                    <div className="font-bold text-xs">
                      {quote.deliveryTimeEstimate}
                    </div>
                  </div>
              </div>
            }
          </div>

          <div className="flex justify-end space-x-4 p-4">
            <div>
              <div className="font-medium text-xs text-gray-600">Total</div>
              <div className="font-bold text-lg">
                {quote.products
                  .filter(({ selected }) => selected)
                  .reduce((memo, product) => memo + (product.price || product.total), 0)}{' '}
                {quote.currency}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
